// Filtros de entrega.
//
// A ORDEM é regra, não apresentação: as regras são avaliadas nela, e uma que
// "para" impede as seguintes. Por isso a tela tem setas de mover, e por isso
// salvar manda a LISTA INTEIRA — reordenar com várias chamadas deixaria a
// lista inconsistente se uma falhasse no meio.

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as conta from "../api/conta";
import * as correio from "../api/correio";
import { ErroDaAPI, explicar } from "../api/problema";
import { Icone } from "../ui/Icone";
import { Aviso, Secao } from "./DoisFatores";
import { useNaoSalvo } from "./naoSalvo";

type Filtro = NonNullable<Awaited<ReturnType<typeof conta.filtros>>>[number];

// `sempre` vem PRIMEIRO porque é o caso mais comum de quem abre esta tela
// para criar um encaminhamento — e porque, em último lugar, ninguém o acha.
//
// Sem ele, "encaminhar tudo" obrigava a inventar uma condição, e a condição
// à mão (`Destinatário contém meu@endereço`) vira `header :contains "to"` no
// Sieve: deixa Cc e Bcc de fora, em silêncio.
// O QUE A REGRA EXAMINA, no seletor de cima.
//
// `sempre` vem PRIMEIRO porque é o caso mais comum de quem abre esta tela
// para criar um encaminhamento — e porque, em último lugar, ninguém o acha.
//
// Sem ele, "encaminhar tudo" obrigava a inventar uma condição, e a condição
// à mão (`Destinatário contém meu@endereço`) vira `header :contains "to"` no
// Sieve: deixa Cc e Bcc de fora, em silêncio.
const TIPOS = [
  ["sempre", "Toda mensagem"],
  ["condicoes", "Mensagem que…"],
] as const;

/** O que cada CONDIÇÃO pode examinar. */
const CAMPOS = [
  ["de", "Remetente"],
  ["para", "Destinatário"],
  ["assunto", "Assunto"],
  ["corpo", "Corpo da mensagem"],
] as const;

type Condicao = NonNullable<Filtro["condicoes"]>[number];

const CONDICAO_NOVA: Condicao = { campo: "de", operador: "contem", valor: "" };

const OPERADORES = [
  ["contem", "contém"],
  ["igual", "é exatamente"],
  ["comeca", "começa com"],
  ["termina", "termina com"],
] as const;

const ACOES = [
  ["mover", "mover para a pasta"],
  ["marcar_estrela", "marcar com estrela"],
  ["marcar_lida", "marcar como lida"],
  ["encaminhar", "encaminhar para"],
  ["apagar", "apagar"],
] as const;

const NOVO: Filtro = {
  nome: "",
  ativo: true,
  // Regra nova nasce CONDICIONAL com uma condição em branco: nascer `sempre`
  // faria o botão "Nova regra" criar um encaminhador de tudo, que é a regra
  // mais perigosa da lista, por padrão.
  campo: "condicoes",
  combinacao: "e",
  condicoes: [{ ...CONDICAO_NOVA }],
  acao: "mover",
  destino: "",
  parar: false,
};

export function Filtros() {
  const fila = useQueryClient();
  const [lista, setLista] = useState<Filtro[]>([]);

  // As pastas que EXISTEM, para a ação "mover".
  //
  // Mesma chave da barra lateral: o cache é o mesmo, então abrir esta tela
  // não custa uma requisição nova. E `rotulo` é o nome traduzido que a
  // pessoa reconhece ("Enviados"), enquanto `nome` é o que o Sieve precisa
  // ("Sent") — mostrar um e gravar o outro é exatamente o que o campo livre
  // não conseguia fazer.
  const pastas = useQuery({
    queryKey: ["pastas"],
    queryFn: ({ signal }) => correio.pastas(signal),
  });
  const [tocado, setTocado] = useState(false);
  const [erro, setErro] = useState("");
  const [porCampo, setPorCampo] = useState<Record<string, string[]>>({});
  const [pronto, setPronto] = useState(false);

  const atual = useQuery({ queryKey: ["conta", "filtros"], queryFn: () => conta.filtros() });

  useEffect(() => {
    if (!tocado && atual.data) setLista(atual.data);
  }, [atual.data, tocado]);

  const salvar = useMutation({
    mutationFn: () => conta.salvarFiltros(lista),
    onSuccess: (d) => {
      setErro("");
      setPorCampo({});
      setPronto(true);
      // O CACHE precisa receber a resposta antes de soltar o "tocado". Sem
      // isto o efeito de preenchimento reaplica o valor ANTIGO por cima do
      // que acabou de ser salvo — a regra sumia da tela tendo sido gravada.
      if (d) fila.setQueryData(["conta", "filtros"], d);
      setLista(d ?? []);
      setTocado(false);
    },
    onError: (e) => {
      setPronto(false);
      setErro(explicar(e));
      // O 422 traz o erro do COMPILADOR Sieve quando a regra não monta. É a
      // mensagem que diz qual regra está errada — mostrá-la é a diferença
      // entre "não deu" e "a regra 3 tem endereço inválido".
      setPorCampo(e instanceof ErroDaAPI ? e.porCampo : {});
    },
  });

  function mudaCondicao(i: number, j: number, mudanca: Partial<Condicao>) {
    setLista((a) =>
      a.map((f, k) =>
        k === i
          ? {
              ...f,
              condicoes: (f.condicoes ?? []).map((c, m) =>
                m === j ? { ...c, ...mudanca } : c,
              ),
            }
          : f,
      ),
    );
    setTocado(true);
    setPronto(false);
  }

  function acrescentaCondicao(i: number) {
    setLista((a) =>
      a.map((f, k) =>
        k === i
          ? { ...f, condicoes: [...(f.condicoes ?? []), { ...CONDICAO_NOVA }] }
          : f,
      ),
    );
    setTocado(true);
    setPronto(false);
  }

  function removeCondicao(i: number, j: number) {
    setLista((a) =>
      a.map((f, k) =>
        k === i
          ? { ...f, condicoes: (f.condicoes ?? []).filter((_, m) => m !== j) }
          : f,
      ),
    );
    setTocado(true);
    setPronto(false);
  }

  function muda(i: number, mudanca: Partial<Filtro>) {
    setLista((a) => a.map((f, k) => (k === i ? { ...f, ...mudanca } : f)));
    setTocado(true);
    setPronto(false);
  }

  function mover(i: number, direcao: -1 | 1) {
    const j = i + direcao;
    if (j < 0 || j >= lista.length) return;
    const copia = [...lista];
    [copia[i], copia[j]] = [copia[j]!, copia[i]!];
    setLista(copia);
    setTocado(true);
    setPronto(false);
  }

  const mensagensGerais = Object.entries(porCampo)
    .filter(([campo]) => !campo.startsWith("filtros."))
    .flatMap(([, msgs]) => msgs);

  // A lista inteira contra a que veio do servidor. Acrescentar, remover e
  // REORDENAR são todas alterações aqui — e a ordem é regra nesta tela, não
  // apresentação, então mover uma regra para cima é uma alteração a salvar
  // como qualquer outra.
  const naoSalvo = useNaoSalvo(
    "Filtros",
    JSON.stringify(lista) !== JSON.stringify(atual.data ?? []),
  );

  return (
    <Secao titulo="Filtros" icone="Filter" naoSalvo={naoSalvo}>
      <p className="text-sm text-tinta-media">
        Agem quando a mensagem chega, mesmo com o webmail fechado — são regras
        do servidor, não do navegador. São avaliadas <strong>de cima para
        baixo</strong>.
      </p>

      {lista.length === 0 && (
        <Aviso tom="neutro">Nenhuma regra. As mensagens chegam à caixa de entrada.</Aviso>
      )}

      <ul className="mt-4 grid gap-3">
        {lista.map((f, i) => (
          <li key={i} className="rounded-lg border border-linha p-4">
            <div className="flex items-center gap-2">
              <input
                value={f.nome ?? ""}
                onChange={(e) => muda(i, { nome: e.target.value })}
                placeholder={`Regra ${i + 1}`}
                className="flex-1 rounded border border-linha px-2 py-1 text-sm font-medium"
              />
              <label className="flex items-center gap-1 text-xs text-tinta-fraca">
                <input
                  type="checkbox"
                  checked={!!f.ativo}
                  onChange={(e) => muda(i, { ativo: e.target.checked })}
                />
                ativa
              </label>
              <button
                onClick={() => mover(i, -1)}
                disabled={i === 0}
                aria-label="Subir"
                className="rounded p-1 hover:bg-fundo disabled:opacity-30"
              >
                <Icone nome="ChevronDown" tamanho={14} className="rotate-180" />
              </button>
              <button
                onClick={() => mover(i, 1)}
                disabled={i === lista.length - 1}
                aria-label="Descer"
                className="rounded p-1 hover:bg-fundo disabled:opacity-30"
              >
                <Icone nome="ChevronDown" tamanho={14} />
              </button>
              <button
                onClick={() => {
                  setLista((a) => a.filter((_, k) => k !== i));
                  setTocado(true);
                }}
                aria-label="Remover regra"
                className="rounded p-1 text-perigo hover:bg-fundo"
              >
                <Icone nome="X" tamanho={14} />
              </button>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-tinta-fraca">Se</span>
              <select
                value={f.campo}
                onChange={(e) => {
                  const campo = e.target.value as Filtro["campo"];
                  // Trocar para `sempre` LIMPA as condições, e trocar de
                  // volta devolve uma em branco. O que estava digitado some
                  // da tela mas continuaria no objeto, e o servidor recusaria
                  // com um erro que a pessoa não consegue ver — o banco
                  // recusa condição em regra `sempre`.
                  muda(i, campo === "sempre"
                    ? { campo, condicoes: [] }
                    : { campo, condicoes: f.condicoes?.length ? f.condicoes : [{ ...CONDICAO_NOVA }] });
                }}
                aria-label="O que a regra examina"
                className="rounded border border-linha px-2 py-1"
              >
                {TIPOS.map(([v, r]) => (
                  <option key={v} value={v}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            {/* AS CONDIÇÕES (apontamento 25: "só uma condição por regra, não
                dá para combinar critérios").
                `sempre` não compara nada: mostrar condição seria oferecer
                campo sem efeito, e campo sem efeito é a definição de
                interface que engana. */}
            {f.campo !== "sempre" && (
              <div className="mt-2 space-y-2">
                {(f.condicoes ?? []).map((c, j) => (
                  <div key={j} className="flex flex-wrap items-center gap-2 text-sm">
                    {/* A JUNÇÃO APARECE ENTRE AS CONDIÇÕES, e é editável em
                        qualquer uma — mas vale para a REGRA inteira, não
                        para o par. `anyof` e `allof` do Sieve não aninham
                        por par, e oferecer "A e B ou C" na tela prometeria
                        uma precedência que o script não tem. Daí o seletor
                        mudar todas de uma vez, e o rótulo dizer "todas"
                        ou "qualquer". */}
                    {j === 0 ? (
                      <span className="w-16 text-right text-xs text-tinta-fraca">
                        a mensagem
                      </span>
                    ) : (
                      <select
                        value={f.combinacao ?? "e"}
                        onChange={(e) =>
                          muda(i, { combinacao: e.target.value as Filtro["combinacao"] })
                        }
                        aria-label="Como as condições se combinam"
                        className="w-16 rounded border border-linha px-1 py-1 text-xs"
                      >
                        <option value="e">e</option>
                        <option value="ou">ou</option>
                      </select>
                    )}
                    <select
                      value={c.campo}
                      onChange={(e) => mudaCondicao(i, j, { campo: e.target.value as Condicao["campo"] })}
                      aria-label={`Campo da condição ${j + 1}`}
                      className="rounded border border-linha px-2 py-1"
                    >
                      {CAMPOS.map(([v, r]) => (
                        <option key={v} value={v}>
                          {r}
                        </option>
                      ))}
                    </select>
                    <select
                      value={c.operador}
                      onChange={(e) => mudaCondicao(i, j, { operador: e.target.value as Condicao["operador"] })}
                      aria-label={`Operador da condição ${j + 1}`}
                      className="rounded border border-linha px-2 py-1"
                    >
                      {OPERADORES.map(([v, r]) => (
                        <option key={v} value={v}>
                          {r}
                        </option>
                      ))}
                    </select>
                    <input
                      value={c.valor}
                      onChange={(e) => mudaCondicao(i, j, { valor: e.target.value })}
                      placeholder="o quê"
                      aria-label={`Valor da condição ${j + 1}`}
                      className="min-w-[160px] flex-1 rounded border border-linha px-2 py-1"
                    />
                    {/* Remover só aparece com mais de uma: regra `condicoes`
                        sem condição nenhuma gera `if  {`, e o Dovecot recusa
                        o script INTEIRO — derrubando os outros filtros da
                        caixa junto. O servidor recusa com 422; a tela nem
                        oferece. */}
                    {(f.condicoes ?? []).length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCondicao(i, j)}
                        aria-label={`Remover a condição ${j + 1}`}
                        className="rounded p-1 text-perigo hover:bg-fundo"
                      >
                        <Icone nome="X" tamanho={13} />
                      </button>
                    )}
                    {porCampo[`filtros.${i}.condicoes.${j}.valor`] && (
                      <span className="w-full text-xs text-perigo">
                        {porCampo[`filtros.${i}.condicoes.${j}.valor`]![0]}
                      </span>
                    )}
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => acrescentaCondicao(i)}
                  // Teto de 10, o mesmo do servidor: um `allof` enorme é
                  // recusado pelo compilador do Sieve, e aí a caixa fica sem
                  // filtro NENHUM.
                  disabled={(f.condicoes ?? []).length >= 10}
                  className="ml-[4.5rem] flex items-center gap-1 rounded-full border border-linha px-2.5 py-1 text-xs text-acao hover:bg-acao-clara disabled:opacity-40"
                >
                  <Icone nome="Plus" tamanho={12} />
                  {(f.condicoes ?? []).length === 1
                    ? "Acrescentar condição"
                    : `Acrescentar (${(f.condicoes ?? []).length} de 10)`}
                </button>

                {/* O QUE A COMBINAÇÃO SIGNIFICA, por extenso. "e" e "ou"
                    entre caixinhas são lidos como ligação do par; aqui fica
                    dito que valem para a regra inteira. */}
                {(f.condicoes ?? []).length > 1 && (
                  <p className="ml-[4.5rem] text-xs text-tinta-fraca">
                    {f.combinacao === "ou"
                      ? "A regra age quando QUALQUER uma das condições acima casar."
                      : "A regra age só quando TODAS as condições acima casarem."}
                  </p>
                )}

                {porCampo[`filtros.${i}.condicoes`] && (
                  <p className="ml-[4.5rem] text-xs text-perigo">
                    {porCampo[`filtros.${i}.condicoes`]![0]}
                  </p>
                )}
              </div>
            )}

            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-tinta-fraca">então</span>
              <select
                value={f.acao}
                onChange={(e) => muda(i, { acao: e.target.value as Filtro["acao"] })}
                aria-label="Ação"
                className="rounded border border-linha px-2 py-1"
              >
                {ACOES.map(([v, r]) => (
                  <option key={v} value={v}>
                    {r}
                  </option>
                ))}
              </select>
              {/* A PASTA VEM DA LISTA DAS QUE EXISTEM, não de um campo livre.
                  Apontamento 23 da revisão de UX. O campo pedia "Nome da
                  pasta", e nome de pasta aqui vira CÓDIGO que roda a cada
                  mensagem que chega: um erro de digitação faz o Sieve
                  arquivar numa pasta inexistente, e o que acontece então é
                  decisão do Dovecot, não da pessoa — na melhor hipótese a
                  mensagem fica na INBOX, e a regra parece não ter rodado.
                  Também não havia como saber a grafia exata: as pastas do
                  sistema são `Sent`, `Junk`, `Trash`, em inglês, enquanto a
                  tela as mostra traduzidas. */}
              {f.acao === "mover" && (
                <select
                  value={f.destino ?? ""}
                  onChange={(e) => muda(i, { destino: e.target.value })}
                  aria-label="Pasta de destino"
                  className="min-w-[180px] flex-1 rounded border border-linha px-2 py-1"
                >
                  <option value="">Escolha a pasta…</option>
                  {(pastas.data?.pastas ?? []).map((p) => (
                    <option key={p.nome} value={p.nome}>
                      {p.rotulo || p.nome}
                    </option>
                  ))}
                </select>
              )}
              {f.acao === "encaminhar" && (
                <input
                  value={f.destino ?? ""}
                  onChange={(e) => muda(i, { destino: e.target.value })}
                  type="email"
                  placeholder="endereco@exemplo.com"
                  aria-label="Encaminhar para"
                  className="min-w-[180px] flex-1 rounded border border-linha px-2 py-1"
                />
              )}
              <label className="flex items-center gap-1 text-xs text-tinta-fraca">
                <input
                  type="checkbox"
                  checked={!!f.parar}
                  onChange={(e) => muda(i, { parar: e.target.checked })}
                />
                e não aplicar as próximas regras
              </label>
            </div>

            {/* O AVISO DO "APAGAR" (apontamento 26).
                Esta ação é a única da lista que não tem volta: o Sieve
                `discard` descarta a mensagem na ENTREGA, antes de ela existir
                em pasta nenhuma — não vai para a Lixeira, não dá para
                recuperar, e não há registro de que chegou. A pessoa escolhe
                "apagar" num seletor igual aos outros quatro e nada diz isso.
                O aviso fica NA REGRA, aparece ao escolher, e diz o que a
                alternativa segura seria. Não é um `confirm()`: a escolha é
                reversível enquanto não se salva, e o que falta é informação,
                não uma trava. */}
            {f.acao === "apagar" && (
              <p className="mt-2 flex items-start gap-2 rounded-lg border border-perigo/40 bg-perigo/10 px-3 py-2 text-xs text-tinta">
                <Icone nome="AlertTriangle" tamanho={14} className="mt-0.5 flex-shrink-0 text-perigo" />
                <span>
                  <strong>Apagar não tem volta.</strong> A mensagem é descartada
                  na entrega: não passa pela Lixeira, não aparece em pasta
                  nenhuma e não há como recuperá-la depois — nem por aqui, nem
                  no Outlook, nem no celular. Se a intenção é tirar da frente,
                  prefira <em>mover para a pasta</em> Lixeira ou Spam, que você
                  ainda consegue abrir.
                </span>
              </p>
            )}


            {porCampo[`filtros.${i}.destino`] && (
              <p className="mt-2 text-xs text-perigo">{porCampo[`filtros.${i}.destino`]![0]}</p>
            )}
          </li>
        ))}
      </ul>

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => {
            setLista((a) => [...a, { ...NOVO }]);
            setTocado(true);
          }}
          className="rounded-lg border border-linha px-4 py-2 text-sm hover:bg-fundo"
        >
          Nova regra
        </button>
        <button
          onClick={() => salvar.mutate()}
          disabled={salvar.isPending}
          className="rounded-lg bg-acao px-4 py-2 text-sm text-white disabled:opacity-60"
        >
          {salvar.isPending ? "…" : "Salvar regras"}
        </button>
        {pronto && <span className="text-sm text-acao">Salvas e no ar.</span>}
      </div>

      {mensagensGerais.length > 0 && (
        <div className="mt-3 rounded-lg border border-perigo/40 bg-perigo/10 px-3 py-2 text-sm text-tinta">
          {mensagensGerais.map((m) => (
            <p key={m}>{m}</p>
          ))}
        </div>
      )}
      {erro && mensagensGerais.length === 0 && <p className="mt-3 text-sm text-perigo">{erro}</p>}
    </Secao>
  );
}
